import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import type { TWAFlow } from "../Integrations"
import type { IWhatsAppTemplateEditorValue } from "../WhatsAppMessages"

export type TAutomationStartType = "event" | "schedule"
export type TAutomationRuleGroupOperator = "and" | "or"
export type TAutomationRuleOperator =
	| "equals"
	| "not_equals"
	| "is_null"
	| "is_not_null"
	| "contains"
	// Typed text against a list of keywords, read as words: case,
	// punctuation and emoji never decide it.
	| "matches_keyword"
	| "contains_keyword"
	| "in"
	| "greater_than"
	| "greater_than_or_equal"
	| "less_than"
	| "less_than_or_equal"
	| "before"
	| "after"

export type TAutomationSourceField = {
	key: string
	label: string
	type: string
	path?: string
	usages?: Array<string>
	recipient_types?: Array<"phone" | "email">
	options?: Array<{
		label: string
		value: string | number | boolean
	}>
}

export type TAutomationSourceVariable = {
	key: string
	label: string
	placeholder: string
	sample_value: string
	description?: string
	value_type?: string
}

export type TAutomationIntervalWindow = {
	start_offset?: string
	end_offset?: string
	every?: string
}

export type TAutomationSendWindow = {
	from?: string
	to?: string
}

export type TAutomationScheduleDefaults = {
	field?: string
	checkpoints?: Array<string>
	interval_window?: TAutomationIntervalWindow | null
	send_window?: TAutomationSendWindow | null
}

export type TAutomationSourceEvent = {
	key: string
	label: string
}

/**
 * A default header is either plain text, or a media header naming the
 * data-source variable that holds its URL, so the attachment resolves per
 * recipient at send time instead of being a fixed file.
 */
export type TAutomationTemplateDefaultWhatsAppHeader =
	| string
	| {
			type: "image" | "video" | "document"
			source_key: string
			document_filename?: string
	  }

export type TAutomationTemplateDefaultWhatsAppTemplate = {
	name: string
	header?: TAutomationTemplateDefaultWhatsAppHeader
	body?: string
	footer?: string
	buttons?: Array<{
		type: string
		text?: string
		url?: string
		phone_number?: string
		code?: string
		url_source_key?: string
	}>
}

export type TAutomationTemplateDataWildcardConfig = {
	prefix: string
	label?: string
	description?: string
}

export type TAutomationTemplateDataSource = {
	key: string
	name: string
	description?: string
	available_parameters: Array<TAutomationSourceVariable>
	wildcard_key_config?: TAutomationTemplateDataWildcardConfig | null
}

export type TAutomationTemplateField = {
	key: string
	label: string
	type:
		| "whatsapp_template"
		| "whatsapp_phone_number"
		| "offset_list"
		| "message_sequence"
		| "rule_group"
		| "interval_window"
		| "send_window"
		| "text"
		| "textarea"
		// A Flow id, picked from the account's published Flows.
		| "whatsapp_flow"
		// An on/off option of the recipe, sent as 1 or 0.
		| "boolean"
	required?: boolean
	description?: string
	placeholder?: string
	/**
	 * Key of a `whatsapp_template` field whose quick replies are this field's
	 * options. Set when the field names a button on that template.
	 */
	buttons_from?: string
	/**
	 * On a `message_sequence` field, what each message may be. Absent means
	 * templates only, which is every recipe that does not fire on something
	 * the customer just did: Meta takes a free-form reply only inside the 24
	 * hours after they last wrote.
	 */
	message_kinds?: Array<TAutomationMessageKind>
}

export type TAutomationMessageKind =
	| "template"
	| "text"
	| "flow"
	// One of the tenant's saved quick sends, sent in order.
	| "quick_send"

export type TAutomationTemplateWhatsAppTemplateOption = {
	id: number
	name: string
	header?: string | null
	body?: string | null
	components?: Array<{
		type: string
		format?: string
		text?: string
		buttons?: Array<Record<string, unknown>>
	}>
	preview?: {
		header_type?: string | null
		header?: string | null
		body?: string | null
		footer?: string | null
		buttons?: Array<{
			type?: string | null
			text?: string | null
			url?: string | null
			phone_number?: string | null
		}>
	}
	status?: string | null
	language?: string | null
	category?: string | null
	/**
	 * The source this template's variables are filled from, and the variables
	 * themselves — the pair the form judges whether this automation can send
	 * it. Same fields the templates listing carries.
	 */
	template_data_source_key?: string | null
	template_data_source_name?: string | null
	required_parameter_names?: Array<string>
	wa_business_account_id?: number | null
	wa_business_account_name?: string | null
	wa_phone_number_id?: number | null
	phone_number?: TAutomationTemplatePhoneNumberOption | null
}

export type TAutomationTemplatePhoneNumberOption = {
	id: number
	name?: string | null
	status?: string | null
	display_phone_number?: string | null
	selection_label?: string | null
	wa_phone_number_id?: number | string | null
	wa_business_account_id?: number | null
	wa_business_account_name?: string | null
}

/**
 * What the catalogue lists: one card's worth per recipe. The fields,
 * defaults, editor seed and catalogues arrive with the detail read of the
 * one recipe picked (`TAutomationTemplate`).
 */
export type TAutomationTemplateSummary = {
	key: string
	name: string
	description?: string
	source_key: string
	// The source's display name, what the catalogue groups cards under.
	source_name?: string | null
	parameters_source_key: string
	channel: "whatsapp"
	version: string
	// Whether the recipe sends a WhatsApp template of its own. Only such a
	// recipe can be linked from the WhatsApp template editor.
	sends_whatsapp_template?: boolean
	supports_filters?: boolean
}

export type TAutomationTemplate = TAutomationTemplateSummary & {
	defaults?: Record<string, unknown>
	configurable_fields: Array<TAutomationTemplateField>
	default_whatsapp_template: TAutomationTemplateDefaultWhatsAppTemplate
	/**
	 * The editor state this template starts from, already in editor shape.
	 * Prefer it over deriving one from `default_whatsapp_template` — the raw
	 * defaults describe intent, not editor fields, and re-deriving them here
	 * is how the two drift apart.
	 */
	default_whatsapp_template_editor?: IWhatsAppTemplateEditorValue | null
	template_data_source?: TAutomationTemplateDataSource | null
	/**
	 * The source's field catalogue, sent only by templates that let the
	 * operator add conditions. Same shape the advanced builder works from, so
	 * the rule editor reads it without translation.
	 */
	filter_source?: TAutomationSource | null
	selected_whatsapp_template?: TAutomationTemplateWhatsAppTemplateOption | null
}

export type TAutomationStepReferenceField = {
	path: string
	label: string
	type: string
	operators?: Array<TAutomationRuleOperator>
	description?: string
}

export type TAutomationSourceActionType =
	| "communication"
	| "create_task"
	| "create_trip_plan_request"
	| "send_whatsapp_flow"
	| "send_whatsapp_text"
	| "send_wa_quick_send"

export type TAutomationSupportedSteps = {
	wait?: {
		modes?: Array<{
			key: TAutomationWaitMode
			label: string
			known_output_fields?: Array<TAutomationStepReferenceField>
			known_meta_fields?: Array<TAutomationStepReferenceField>
		}>
	}
	action?: {
		types?: Array<{
			key: TAutomationSourceActionType
			label: string
			known_output_fields?: Array<TAutomationStepReferenceField>
			known_meta_fields?: Array<TAutomationStepReferenceField>
		}>
	}
	condition?: {
		label?: string
	}
}

export type TAutomationRetryMode = "resume_from_failed_step" | "start_over"

export type TAutomationRunMode = "live" | "dry_run"

export type TAutomationRetryInfo = {
	mode?: TAutomationRetryMode | null
	retried_from_run_id?: number | null
	root_run_id?: number | null
	attempt?: number | null
	resumed_from_step_key?: string | null
	retried_at?: string | null
}

export type TAutomationEventProvenance = {
	name?: string | null
	changed_fields?: Array<string>
	fingerprint?: string | null
}

export type TAutomationScheduleProvenance = {
	automation_scheduled_run_id?: number | null
	checkpoint_key?: string | null
	scheduled_for?: string | null
	mode?: string | null
	field?: string | null
	occurrence_index?: number | null
}

export type TAutomationRunProvenance = {
	type?: string | null
	event?: TAutomationEventProvenance | null
	schedule?: TAutomationScheduleProvenance | null
}

export type TAutomationWildcardField = {
	prefix: string
	label: string
	type?: string
	usages?: Array<string>
	recipient_types?: Array<"phone" | "email">
	description?: string
}

export type TAutomationSource = {
	key: string
	name: string
	description?: string
	supported_start_types: Array<TAutomationStartType>
	supported_events: Array<TAutomationSourceEvent>
	filter_fields: Array<TAutomationSourceField>
	schedule_fields: Array<TAutomationSourceField>
	wait_fields: Array<TAutomationSourceField>
	recipient_fields: Array<TAutomationSourceField>
	assignable_user_fields: Array<TAutomationSourceField>
	available_parameters: Array<TAutomationSourceVariable>
	supported_steps?: TAutomationSupportedSteps
	supported_actions: Array<TAutomationSourceActionType>
	supported_channels: Array<"whatsapp" | "email">
	// Whether the trigger record is a WhatsApp conversation being answered.
	// Such a reply goes out from the number the customer wrote to, so no
	// step on this source asks for a sender.
	replies_into_conversation?: boolean
	// Whether an event automation may stay quiet for a while after it runs,
	// for every record sharing a scope with the one it ran for (the
	// conversation, for a WhatsApp reply).
	supports_cooldown?: boolean
	schedule_defaults?: TAutomationScheduleDefaults | null
	wildcard_field?: TAutomationWildcardField | null
}

export type TAutomationRule = {
	field_path: string
	operator: TAutomationRuleOperator
	value?: string | number | boolean | Array<string | number | boolean> | null
}

export type TAutomationRuleGroup = {
	operator: TAutomationRuleGroupOperator
	rules: Array<TAutomationRule | TAutomationRuleGroup>
}

export type TAutomationEventStart = {
	event: string
	filters?: TAutomationRuleGroup
	// Minutes to stay quiet after a run; only on a source that supports it.
	cooldown_minutes?: number
}

export type TAutomationScheduleStart = {
	field: string
	checkpoints?: Array<string>
	interval_window?: TAutomationIntervalWindow | null
	send_window?: TAutomationSendWindow | null
	filters?: TAutomationRuleGroup
}

export type TAutomationWaitMode = "relative_to_trigger" | "relative_to_field"

export type TAutomationWaitStepConfig = {
	mode: TAutomationWaitMode
	field?: string
	offset?: string
}

export type TAutomationRecipientResolver =
	| {
			type: "field_path"
			path: string
			recipient_type: "phone" | "email"
	  }
	| {
			type: "static"
			value: string
			recipient_type: "phone" | "email"
	  }

export type TAutomationWhatsAppDelivery = {
	channel: "whatsapp"
	template_id: number
	/**
	 * Absent on a source that answers a conversation: the message goes out
	 * from the number the customer wrote to, so the step carries no sender of
	 * its own and the server stores none.
	 */
	phone_number_id?: number
	integrated_app_id?: number | null
}

/**
 * Offer a WhatsApp Flow, a form, as a free-form bubble to the step's
 * recipients. A step of its own: it sends no template.
 */
export type TAutomationSendWhatsAppFlowAction = {
	type: "send_whatsapp_flow"
	recipients?: TAutomationCommunicationAction["recipients"]
	flow: {
		flow_id: string
		cta: string
		body: string
		header?: string
		footer?: string
		screen?: string
		/** Absent on a reply source, on the same terms as a delivery's. */
		phone_number_id?: number
		integrated_app_id?: number | null
	}
}

/**
 * Send a message the operator wrote, placeholders resolved from the source.
 * Free-form, so only delivered inside the customer service window.
 */
export type TAutomationSendWhatsAppTextAction = {
	type: "send_whatsapp_text"
	recipients?: TAutomationCommunicationAction["recipients"]
	text: {
		body: string
		/** Absent on a reply source, on the same terms as a delivery's. */
		phone_number_id?: number
		integrated_app_id?: number | null
	}
}

/**
 * Send one of the tenant's saved quick sends, its messages in order. Free-form
 * like the Flow step, so only offered on a source the customer's own message
 * triggers.
 */
export type TAutomationSendWAQuickSendAction = {
	type: "send_wa_quick_send"
	recipients?: TAutomationCommunicationAction["recipients"]
	quick_send: {
		quick_send_id: number
		/** Absent on a reply source, on the same terms as a delivery's. */
		phone_number_id?: number
		integrated_app_id?: number | null
	}
}

export type TAutomationCreateTripPlanRequestAction = {
	type: "create_trip_plan_request"
	trip_source?: string | null
}

export type TAutomationCommunicationAction = {
	type: "communication"
	recipients?: {
		to?: Array<TAutomationRecipientResolver>
		cc?: Array<TAutomationRecipientResolver>
		bcc?: Array<TAutomationRecipientResolver>
	}
	deliveries?: Array<TAutomationWhatsAppDelivery>
}

export type TAutomationTaskAssignment =
	| {
			type: "static_user"
			user_id: number
	  }
	| {
			type: "field_path"
			path: string
	  }

export type TAutomationTaskDue =
	| {
			mode: "relative_to_trigger"
			offset: string
	  }
	| {
			mode: "relative_to_field"
			field: string
			offset: string
	  }

export type TAutomationCreateTaskAction = {
	type: "create_task"
	body_template: string
	assign_to?: TAutomationTaskAssignment | null
	due?: TAutomationTaskDue | null
}

export type TAutomationActionStepConfig = {
	action:
		| TAutomationCommunicationAction
		| TAutomationCreateTaskAction
		| TAutomationCreateTripPlanRequestAction
		| TAutomationSendWhatsAppFlowAction
		| TAutomationSendWAQuickSendAction
		| TAutomationSendWhatsAppTextAction
}

export type TAutomationConditionStepConfig = TAutomationRuleGroup

export type TAutomationStepConfig =
	| TAutomationActionStepConfig
	| TAutomationWaitStepConfig
	| TAutomationConditionStepConfig
	| Record<string, unknown>

export type TAutomationStep = {
	id?: number
	key: string
	type: "wait" | "action" | "condition" | "branch"
	parent_id?: number | null
	order_index?: number
	position: number
	config: TAutomationStepConfig
	is_active?: boolean
	children?: Array<TAutomationStep>
}

export type TAutomation = {
	id: number
	source_key: string
	start_type: TAutomationStartType
	start?: TAutomationEventStart | TAutomationScheduleStart | null
	template_key?: string | null
	template_version?: string | null
	template_inputs?: Record<string, unknown> | null
	template?: TAutomationTemplateSummary | null
	source?: TAutomationSource | null
	is_active: boolean
	total_runs_count: number
	steps_count?: number
	last_run_at?: string | null
	steps?: Array<TAutomationStep>
	created_by?: number | null
	created_at: string
	updated_at?: string | null
}

export type TAutomationRun = {
	id: number
	automation_id: number
	automation?: Pick<
		TAutomation,
		"id" | "source_key" | "is_active" | "source"
	> | null
	source_key: string
	run_mode: TAutomationRunMode
	triggered_by_type?: string | null
	triggered_by_id?: number | null
	trigger?: { wa_thread_id?: number | null } | null
	event_name?: string | null
	event_changed_fields?: Array<string>
	provenance?: TAutomationRunProvenance | null
	scheduled_run?: {
		id: number
		checkpoint_key?: string | null
		scheduled_for?: string | null
		status: string
		payload?: TAutomationScheduledRunPayload | null
		processed_at?: string | null
		failed_at?: string | null
		cancelled_at?: string | null
		error?: string | null
	} | null
	status:
		| "pending"
		| "running"
		| "completed"
		| "failed"
		| "cancelled"
		| "skipped"
	error?: string | null
	retry?: TAutomationRetryInfo | null
	current_step_position?: number | null
	resume_at?: string | null
	context?: Record<string, unknown> | null
	step_runs_count?: number
	started_at?: string | null
	completed_at?: string | null
	failed_at?: string | null
	cancelled_at?: string | null
	created_at: string
	updated_at?: string | null
}

export type TAutomationStepRun = {
	id: number
	automation_run_id: number
	automation_step_id?: number | null
	step_type:
		| "pending"
		| "running"
		| "completed"
		| "failed"
		| "cancelled"
		| string
	status:
		| "pending"
		| "running"
		| "completed"
		| "failed"
		| "cancelled"
		| "skipped"
		| "reused"
	error?: string | null
	input?: Record<string, unknown> | null
	output?: Record<string, unknown> | null
	meta?: Record<string, unknown> | null
	resume_at?: string | null
	started_at?: string | null
	completed_at?: string | null
	failed_at?: string | null
	cancelled_at?: string | null
	automation_step?: Pick<
		TAutomationStep,
		| "id"
		| "key"
		| "type"
		| "parent_id"
		| "order_index"
		| "position"
		| "config"
		| "is_active"
	> | null
	created_at: string
	updated_at?: string | null
}

export type TAutomationRunDetail = TAutomationRun & {
	step_runs?: Array<TAutomationStepRun>
}

export type TAutomationDryRunPayload = {
	source_record_id: number
	simulated_inbound_whatsapp_message?: string | null
}

export type TAutomationScheduledRunPayload = {
	mode?: string
	field?: string
	offset?: string
	start_offset?: string
	end_offset?: string
	every?: string
	window_start_at?: string
	window_end_at?: string
	occurrence_index?: number
	[key: string]: unknown
}

export type TAutomationScheduledRun = {
	id: number
	automation_id: number
	automation?: Pick<
		TAutomation,
		"id" | "source_key" | "is_active" | "source"
	> | null
	automation_run_id?: number | null
	automation_run?: {
		id: number
		source_key: string
		run_mode: TAutomationRunMode
		status:
			| "pending"
			| "running"
			| "completed"
			| "failed"
			| "cancelled"
			| "skipped"
		error?: string | null
		started_at?: string | null
		completed_at?: string | null
		failed_at?: string | null
		cancelled_at?: string | null
	} | null
	triggered_by_type?: string | null
	triggered_by_id?: number | null
	checkpoint_key: string
	mode?: string | null
	field?: string | null
	occurrence_index?: number | null
	scheduled_for: string
	status:
		| "pending"
		| "processing"
		| "completed"
		| "failed"
		| "skipped"
		| "cancelled"
	payload?: TAutomationScheduledRunPayload | null
	error?: string | null
	processed_at?: string | null
	failed_at?: string | null
	cancelled_at?: string | null
	created_at: string
	updated_at?: string | null
}

export type TAutomationPayload = {
	source_key: string
	start_type: TAutomationStartType
	start: TAutomationEventStart | TAutomationScheduleStart
	steps: Array<{
		key: string
		type: "action" | "wait" | "condition"
		position?: number
		config:
			| TAutomationActionStepConfig
			| TAutomationWaitStepConfig
			| TAutomationConditionStepConfig
		children?: Array<{
			key: string
			type: "action" | "wait" | "condition"
			position?: number
			config:
				| TAutomationActionStepConfig
				| TAutomationWaitStepConfig
				| TAutomationConditionStepConfig
			children?: Array<unknown>
		}>
	}>
}

export type TAutomationTemplateMessageChannel = {
	channel: "whatsapp"
	// What the message is. Absent means a template, which is all a recipe
	// offers unless it fires on something the customer just did.
	kind?: TAutomationMessageKind
	whatsapp_template_id?: number
	// Present only on derived input (server-resolved) so the edit form can
	// pre-fill each channel's template picker; omitted from the create body.
	whatsapp_template?: TAutomationTemplateWhatsAppTemplateOption | null
	// A free-text reply.
	body?: string
	// A form offered. `flow` is server-resolved on derived input, the way
	// `whatsapp_template` is.
	flow_id?: number
	flow?: TWAFlow | null
	flow_body?: string
	flow_cta?: string
	flow_screen?: string
	flow_header?: string | null
	flow_footer?: string | null
	// A saved quick send. `quick_send` is server-resolved on derived input,
	// the way `flow` is; null when it has been deleted since.
	quick_send_id?: number
	quick_send?: { id: number; name: string } | null
}

export type TAutomationTemplateMessage = {
	wait_before?: string | null
	// One or more per-channel deliveries. Only WhatsApp is executable today.
	channels: Array<TAutomationTemplateMessageChannel>
}

export type TAutomationTemplatePayload = {
	whatsapp_template_id?: number
	// Only for recipes that send something; a recipe with no message of its
	// own (one that creates a record, say) has no sender.
	phone_number_id?: number
	// Plain inputs declared by the recipe as text fields, keyed by field.
	[textInput: string]: unknown
	checkpoints?: Array<string>
	interval_window?: TAutomationIntervalWindow | null
	send_window?: TAutomationSendWindow | null
	messages?: Array<TAutomationTemplateMessage>
	// Sent whenever the template supports conditions, empty group included, so
	// removing every rule clears the stored ones instead of leaving them.
	filters?: TAutomationRuleGroup
	is_active?: boolean
}

export type TAutomationTemplateAutomation = TAutomationTemplate & {
	automation_id: number
	input: TAutomationTemplatePayload
	stored_input?: Record<string, unknown> | null
	selected_phone_number?: TAutomationTemplatePhoneNumberOption | null
	/**
	 * The Flows behind the recipe's Flow fields, keyed by field. Null for a
	 * field whose Flow is no longer one of ours.
	 */
	selected_flows?: Record<string, TWAFlow | null> | null
	/**
	 * The WhatsApp templates behind the recipe's template-picker fields, keyed
	 * by field. Distinct from `selected_whatsapp_template`, which is the
	 * message the recipe sends rather than one it filters on.
	 */
	selected_templates?: Record<
		string,
		TAutomationTemplateWhatsAppTemplateOption | null
	> | null
}

export function XHR(xhr: XHRInstance) {
	return {
		async get(params: Record<string, unknown>) {
			const resp = await xhr.get<IListResponse<TAutomation>>("/automations", {
				params,
			})
			return resp.data
		},
		async show(id: number | string) {
			const resp = await xhr.get<{ data: TAutomation }>(`/automations/${id}`)
			return resp.data
		},
		async create(data: TAutomationPayload) {
			const resp = await xhr.post<{ data: TAutomation }>("/automations", data)
			return resp.data
		},
		async update(id: number, data: TAutomationPayload) {
			const resp = await xhr.put<{ data: TAutomation }>(
				`/automations/${id}`,
				data
			)
			return resp.data
		},
		async dryRun(id: number | string, data: TAutomationDryRunPayload) {
			const resp = await xhr.post<{ data: TAutomationRun }>(
				`/automations/${id}/dry-run`,
				data
			)
			return resp.data
		},
		async destroy(id: number) {
			const resp = await xhr.delete(`/automations/${id}`)
			return resp.data
		},
		async toggle(id: number | string) {
			const resp = await xhr.patch<{ data: TAutomation }>(
				`/automations/${id}/toggle`
			)
			return resp.data
		},
		async getRuns(params: Record<string, unknown>) {
			const resp = await xhr.get<IListResponse<TAutomationRun>>(
				"/automations/runs",
				{ params }
			)
			return resp.data
		},
		async showRun(id: number | string) {
			const resp = await xhr.get<{ data: TAutomationRunDetail }>(
				`/automations/runs/${id}`
			)
			return resp.data
		},
		async retryRun(id: number | string, mode: TAutomationRetryMode) {
			const resp = await xhr.post<{ data: TAutomationRun }>(
				`/automations/runs/${id}/retry`,
				{ mode }
			)
			return resp.data
		},
		async getSchedules(params: Record<string, unknown>) {
			const resp = await xhr.get<IListResponse<TAutomationScheduledRun>>(
				"/automations/schedules",
				{
					params,
				}
			)
			return resp.data
		},
		async showSchedule(id: number | string) {
			const resp = await xhr.get<{ data: TAutomationScheduledRun }>(
				`/automations/schedules/${id}`
			)
			return resp.data
		},
		async getSources(params: Record<string, unknown>) {
			const resp = await xhr.get<IListResponse<TAutomationSource>>(
				"/automations/sources",
				{
					params,
				}
			)
			return resp.data
		},
		async getTemplates() {
			const resp = await xhr.get<{
				data: Array<TAutomationTemplateSummary>
			}>("/automation-templates")
			return resp.data
		},
		async showTemplate(key: string) {
			const resp = await xhr.get<{ data: TAutomationTemplate }>(
				`/automation-templates/${key}`
			)
			return resp.data
		},
		async createFromTemplate(key: string, data: TAutomationTemplatePayload) {
			const resp = await xhr.post<{ data: TAutomation }>(
				`/automation-templates/${key}/automations`,
				data
			)
			return resp.data
		},
		async showAutomationTemplate(id: number | string) {
			const resp = await xhr.get<{ data: TAutomationTemplateAutomation }>(
				`/automations/${id}/template`
			)
			return resp.data
		},
		async updateFromTemplate(
			id: number | string,
			data: TAutomationTemplatePayload
		) {
			const resp = await xhr.put<{ data: TAutomation }>(
				`/automations/${id}/template`,
				data
			)
			return resp.data
		},
	}
}

export type TAutomationCheckpointDirection = "before" | "after"
export type TAutomationCheckpointUnit = "minutes" | "hours" | "days"

export type TAutomationCheckpointFormValue = {
	id: string
	direction?: TAutomationCheckpointDirection
	amount?: string | number
	unit?: TAutomationCheckpointUnit
}

function createCheckpointId(): string {
	return `checkpoint_${Math.random().toString(36).slice(2, 10)}`
}

export function createCheckpointFormValue(
	partial?: Partial<TAutomationCheckpointFormValue>
): TAutomationCheckpointFormValue {
	return {
		id: createCheckpointId(),
		direction: "before",
		amount: "1",
		unit: "days",
		...partial,
	}
}

// Map a unit token to one the editor supports. The backend stores whatever
// PHP's `Carbon->modify()` accepts, so offsets can arrive with abbreviations
// (e.g. "hrs", "min") that must round-trip through the editor.
function normalizeCheckpointUnit(
	raw: string
): TAutomationCheckpointUnit | null {
	switch (raw.toLowerCase()) {
		case "min":
		case "mins":
		case "minute":
		case "minutes":
			return "minutes"
		case "hr":
		case "hrs":
		case "hour":
		case "hours":
			return "hours"
		case "day":
		case "days":
			return "days"
		default:
			return null
	}
}

// A signed offset string ("+2 days" / "-1 day") <-> the editable checkpoint form
// value. These are the wire format the backend stores in `start.checkpoints`.
export function parseCheckpointString(
	value?: string | null
): TAutomationCheckpointFormValue | null {
	if (!value) {
		return null
	}
	const match = value.trim().match(/^([+-])\s*(\d+)\s*([a-zA-Z]+)$/)

	if (!match) {
		return null
	}

	const [, sign, amount, rawUnit] = match
	const unit = normalizeCheckpointUnit(rawUnit)

	if (!unit) {
		return null
	}

	return createCheckpointFormValue({
		direction: sign === "-" ? "before" : "after",
		amount,
		unit,
	})
}

export function checkpointToOffset(
	checkpoint: TAutomationCheckpointFormValue
): string | null {
	const amount =
		typeof checkpoint.amount === "string"
			? checkpoint.amount.trim()
			: String(checkpoint.amount ?? "").trim()

	if (!/^\d+$/.test(amount) || Number(amount) <= 0) {
		return null
	}

	return `${checkpoint.direction === "before" ? "-" : "+"}${amount} ${checkpoint.unit || "days"}`
}

// Convenience: map a stored offset list to editable checkpoints (always at least
// one so the editor never renders empty), and back to the offset list on submit.
export function offsetsToCheckpoints(
	offsets?: Array<string> | null
): Array<TAutomationCheckpointFormValue> {
	const parsed = (offsets || [])
		.map((offset) => parseCheckpointString(offset))
		.filter((c): c is TAutomationCheckpointFormValue => Boolean(c))
	return parsed.length ? parsed : [createCheckpointFormValue()]
}

export function checkpointsToOffsets(
	checkpoints?: Array<TAutomationCheckpointFormValue> | null
): Array<string> {
	return (checkpoints || [])
		.map((checkpoint) => checkpointToOffset(checkpoint))
		.filter((offset): offset is string => Boolean(offset))
}

// --- Signed offsets ("+2 days" / "-1 day") and repeat intervals ("6 hours") ---
// Used by wait steps, task-due times and the schedule repeat window.

export type TAutomationOffsetFormValue = {
	direction?: TAutomationCheckpointDirection
	amount?: string | number
	unit?: TAutomationCheckpointUnit
}

export type TAutomationEveryFormValue = {
	amount?: string | number
	unit?: TAutomationCheckpointUnit
}

export function createOffsetFormValue(
	partial?: Partial<TAutomationOffsetFormValue>
): TAutomationOffsetFormValue {
	return {
		direction: "after",
		amount: "1",
		unit: "hours",
		...partial,
	}
}

export function createEveryFormValue(
	partial?: Partial<TAutomationEveryFormValue>
): TAutomationEveryFormValue {
	return {
		amount: "6",
		unit: "hours",
		...partial,
	}
}

export function parseOffsetString(
	value?: string | null
): TAutomationOffsetFormValue | null {
	if (!value) {
		return null
	}
	const match = value.trim().match(/^([+-]?)\s*(\d+)\s*([a-zA-Z]+)$/)

	if (!match) {
		return null
	}

	const [, sign, amount, rawUnit] = match
	const unit = normalizeCheckpointUnit(rawUnit)

	if (!unit) {
		return null
	}

	return createOffsetFormValue({
		direction: sign === "-" ? "before" : "after",
		amount,
		unit,
	})
}

export function parseEveryString(
	value?: string | null
): TAutomationEveryFormValue | null {
	if (!value) {
		return null
	}
	const match = value.trim().match(/^(\d+)\s*([a-zA-Z]+)$/)

	if (!match) {
		return null
	}

	const [, amount, rawUnit] = match
	const unit = normalizeCheckpointUnit(rawUnit)

	if (!unit) {
		return null
	}

	return createEveryFormValue({ amount, unit })
}

function trimmedAmount(value: unknown): string {
	return typeof value === "string" ? value.trim() : String(value ?? "").trim()
}

export function offsetToString(
	offset?: Partial<TAutomationOffsetFormValue> | null
): string | null {
	if (!offset) {
		return null
	}

	const amount = trimmedAmount(offset.amount)
	const unit = offset.unit

	if (
		!/^\d+$/.test(amount) ||
		(unit !== "minutes" && unit !== "hours" && unit !== "days")
	) {
		return null
	}

	if (Number(amount) === 0) {
		return `0 ${unit}`
	}

	return `${offset.direction === "before" ? "-" : "+"}${amount} ${unit}`
}

export function everyToString(
	every?: Partial<TAutomationEveryFormValue> | null
): string | null {
	if (!every) {
		return null
	}

	const amount = trimmedAmount(every.amount)
	const unit = every.unit

	if (
		!/^\d+$/.test(amount) ||
		Number(amount) <= 0 ||
		(unit !== "minutes" && unit !== "hours" && unit !== "days")
	) {
		return null
	}

	return `${amount} ${unit}`
}
